<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import PongoFormComparison from '../components/PongoFormComparison.vue'
import DateComparison from '../components/DateComparison.vue'
import AudienceDisclosure from '@/views/audience/components/AudienceDisclosure.vue'
import AudienceContestGameBlock from '../components/AudienceContestGameBlock.vue'
import AudienceSurveyBlock from '../components/AudienceSurveyBlock.vue'

import type { AudienceRule, CustomerTag } from '@/types/types'
import type { RulePrimitive } from '@/types/types'

import type { HighlightInfoFn } from '../composables/rulesMatcher'

const { relativeOperators, isRuleValueValid, isAudienceRuleWithParameters } = useAudience()

const props = withDefaults(
  defineProps<{
    rules?: Record<string, AudienceRule>
    classes: any
    disabled: boolean
    getHighlightInfo: HighlightInfoFn
  }>(),
  {
    rules: () => ({})
  }
)

const emit = defineEmits<{
  (e: 'update:games', value: unknown): void
  (e: 'update:surveys', value: unknown): void
  (e: 'hasRulesError', value: boolean): void
}>()

const { t } = useI18n()

const rules = useVModel(props, 'rules')
const tags: CustomerTag[] = inject('marketingTags')

const pointTotalHighlight = computed(() => props.getHighlightInfo('pointTotal'))
const pointCumulatedHighlight = computed(() => props.getHighlightInfo('pointCumulated'))
const tagsHighlight = computed(() => props.getHighlightInfo('tags'))
const contestGameHighlight = computed(() => props.getHighlightInfo('contestGameParticipation'))
const surveysHighlight = computed(() => props.getHighlightInfo('surveys'))
const webappLogInHighlight = computed(() => props.getHighlightInfo('webappLogIn'))
const walletInstalledHighlight = computed(() => props.getHighlightInfo('canReceivePushNotifications'))
const walletPresenceOptions = computed(() => [
  { id: 'yes', text: t('pages.audience.audienceCreatePage.block.engagement.walletInstalled.yes') },
  { id: 'no', text: t('pages.audience.audienceCreatePage.block.engagement.walletInstalled.no') }
])

const walletInstalledValue = computed<'yes' | 'no' | null>(() => {
  const v = rules.value.canReceivePushNotifications?.value
  if (!Array.isArray(v) || v.length === 0) return null
  return v[0] === true ? 'yes' : 'no'
})

const isButtonDisplayed = ref(true)

const addAdditionalRule = () => {
  if (!isAudienceRuleWithParameters(rules.value.pointCumulated)) {
    rules.value.pointCumulated = {
      ...rules.value.pointCumulated,
      parameters: {
        date_filter: {
          value: [],
          operator: '',
          type: ''
        }
      }
    }
  }
  isButtonDisplayed.value = false
}

const showRelativeButton = computed(() => {
  const isFormReadOnly = props.disabled === true
  const isMainRuleSet = isRuleValueValid(rules.value.pointCumulated.value[0])
  const hasAdditionalRuleValue =
    isAudienceRuleWithParameters(rules.value.pointCumulated) &&
    isRuleValueValid(rules.value.pointCumulated.parameters.date_filter.value)

  if (!isButtonDisplayed.value || hasPointCumulatedInputErrors.value) {
    return false
  }

  if (isFormReadOnly && isMainRuleSet && !hasAdditionalRuleValue) {
    return false
  }

  if (!isFormReadOnly && isMainRuleSet && hasAdditionalRuleValue) {
    return false
  }

  if (isFormReadOnly && isMainRuleSet && hasAdditionalRuleValue) {
    return false
  }

  if (
    !isFormReadOnly &&
    isMainRuleSet &&
    !hasAdditionalRuleValue &&
    isAudienceRuleWithParameters(rules.value.pointCumulated) &&
    rules.value.pointCumulated.parameters.date_filter.operator
  ) {
    return false
  }

  return true
})

const displayRelativeDateBlock = computed(() => {
  const isFormReadOnly = props.disabled === true
  const isRuleValueSet = !!rules.value.pointCumulated.value[0]
  const hasAdditionalRuleValue =
    isAudienceRuleWithParameters(rules.value.pointCumulated) &&
    isRuleValueValid(rules.value.pointCumulated.parameters.date_filter.value)

  if (!isRuleValueSet && isFormReadOnly) {
    return false
  }

  if (!hasAdditionalRuleValue && isFormReadOnly && !isRuleValueSet) {
    return false
  }

  if (isFormReadOnly && isRuleValueSet && !hasAdditionalRuleValue) {
    return false
  }

  return true
})

const resetCheckinCount = () => {
  rules.value.pointCumulated.parameters = {
    date_filter: {
      value: [],
      type: '',
      operator: ''
    }
  }
  isButtonDisplayed.value = true
}

const hasPointCumulatedInputErrors = computed(() => {
  if (
    isAudienceRuleWithParameters(rules.value.pointCumulated) &&
    !isRuleValueValid(rules.value.pointCumulated.value) &&
    isRuleValueValid(rules.value.pointCumulated.parameters?.date_filter?.value)
  ) {
    return true
  }
  return false
})

function toggleValue(ruleKey: string, value: RulePrimitive) {
  const currentValue = rules.value[ruleKey]?.value
  const valueArray = Array.isArray(currentValue) ? currentValue : []
  const set = new Set<RulePrimitive>(valueArray)
  if (set.has(value)) {
    set.delete(value)
  } else {
    set.add(value)
  }
  rules.value[ruleKey] = { ...rules.value[ruleKey], value: Array.from(set) }
}

const selectedContestGameIds = ref<number[]>([])
const selectedSurveysIds = ref<Array<number>>([])

const activeFiltersCount = computed(() => {
  let count = 0
  if (Array.isArray(rules.value.pointTotal?.value) && rules.value.pointTotal.value.length > 0) count++
  if (Array.isArray(rules.value.pointCumulated?.value) && rules.value.pointCumulated.value.length > 0) count++
  if (Array.isArray(rules.value.tags?.value) && rules.value.tags.value.length > 0) count++
  if (
    Array.isArray(rules.value.contestGameParticipation?.value) &&
    rules.value.contestGameParticipation.value.length > 0
  ) {
    count++
  }
  if (Array.isArray(rules.value.surveys?.value) && rules.value.surveys.value.length > 0) count++
  if (selectedContestGameIds.value?.length > 0) count++
  if (selectedSurveysIds.value?.filter((id) => id !== 0).length > 0) count++
  if (Array.isArray(rules.value.webappLogIn?.value) && rules.value.webappLogIn.value.length > 0) count++
  if (
    Array.isArray(rules.value.canReceivePushNotifications?.value) &&
    rules.value.canReceivePushNotifications.value.length > 0
  )
    count++
  return count
})

const webappLogInDropdownUpdate = (value: number | null) => {
  if (value === null) {
    rules.value.webappLogIn.value = []
    return
  }
  rules.value.webappLogIn.value = [value]
}

const walletInstalledDropdownUpdate = (value: string | null | undefined) => {
  if (!value) {
    rules.value.canReceivePushNotifications.value = []
    return
  }
  rules.value.canReceivePushNotifications.operator = 'eq'
  rules.value.canReceivePushNotifications.value = [value === 'yes']
}
</script>

<template>
  <AudienceDisclosure
    :title="t('pages.audience.audienceCreatePage.block.engagement.title')"
    :subtitle="t('pages.audience.audienceCreatePage.block.engagement.description')"
    icon="IconoirActivity"
    :active-filters-count="activeFiltersCount"
  >
    <div
      :class="[
        classes.rowGrid,
        {
          'bg-blue-50/30 rounded-lg p-3 ring-1 ring-blue-600': pointTotalHighlight.hasDefined
        }
      ]"
    >
      <label :class="classes.label">
        {{ t('pages.audience.audienceCreatePage.block.engagement.pointTotal.label') }}
      </label>
      <PongoFormComparison
        :class="classes.inputs"
        :disabled="disabled"
        :rule="rules['pointTotal']"
        name="point-total"
        :min="0"
      />
    </div>
    <div
      :class="[
        classes.rowGrid,
        {
          'bg-blue-50/30 rounded-lg p-3 ring-1 ring-blue-600': pointCumulatedHighlight.hasDefined
        }
      ]"
    >
      <label :class="classes.label">
        {{ t('pages.audience.audienceCreatePage.block.engagement.pointCumulated.label') }}
      </label>
      <PongoFormComparison
        :class="classes.inputs"
        :disabled="disabled"
        :rule="rules['pointCumulated']"
        name="point-cumulated"
        :min="0"
        :errors="hasPointCumulatedInputErrors"
        @has-error="emit('hasRulesError', $event)"
      />
    </div>
    <div v-if="displayRelativeDateBlock" class="grid grid-cols-10">
      <TreeNode class="col-span-8" content-class="w-full">
        <template #content>
          <Button
            v-if="showRelativeButton"
            class="tree-content gap-2"
            type="button"
            variant="ghost"
            :disabled="disabled"
            @click="addAdditionalRule"
          >
            <BaseIcon name="HeroPlusIconSolid" class="h-5 w-5" />
            {{ t('pages.audience.operators.date.conditionBtn') }}
          </Button>
          <DateComparison
            v-else
            is-relative-rule
            :operators="relativeOperators"
            :rule="rules.pointCumulated"
            :disabled="disabled"
          />
        </template>
      </TreeNode>
      <Button
        v-if="!showRelativeButton && !disabled"
        variant="ghost"
        size="icon"
        class="col-start-10 aspect-square w-fit justify-self-end text-muted-foreground hover:text-foreground-secondary"
        @click="resetCheckinCount"
      >
        <BaseIcon name="HeroTrashIconOutline" class="h-5 w-5" />
      </Button>
    </div>

    <div
      :class="[
        classes.rowGrid,
        {
          'bg-blue-50/30 rounded-lg p-3 ring-1 ring-blue-600': tagsHighlight.hasDefined
        }
      ]"
    >
      <label :class="classes.label">
        {{ t('pages.audience.audienceCreatePage.block.engagement.modules.label') }}
      </label>
      <div :class="classes.checkboxesContainer">
        <AppCheckboxField
          v-for="tag in tags"
          :id="`tag-${tag.id}`"
          :key="tag.id"
          :disabled="disabled"
          :model-value="(rules.tags?.value as number[]).includes(tag.id)"
          @update:model-value="() => toggleValue('tags', tag.id)"
        >
          <template #label>
            <BaseChip :text="tag.name" :color="tag.color" />
          </template>
        </AppCheckboxField>
      </div>
    </div>

    <AudienceContestGameBlock
      v-model:rule="rules.contestGameParticipation"
      :disabled="disabled"
      :classes="classes"
      :is-highlighted="contestGameHighlight.hasDefined"
      @update:games="emit('update:games', $event)"
    />

    <AudienceSurveyBlock
      v-model:rule="rules.surveys"
      :disabled="disabled"
      :classes="classes"
      :is-highlighted="surveysHighlight.hasDefined"
      @update:surveys="emit('update:surveys', $event)"
    />
    <div
      :class="[
        classes.rowGrid,
        {
          'bg-blue-50/30 rounded-lg p-3 ring-1 ring-blue-600': webappLogInHighlight.hasDefined
        }
      ]"
    >
      <label class="col-span-5 flex flex-wrap break-words text-sm font-medium leading-normal text-foreground">
        {{ t('pages.audience.audienceCreatePage.block.engagement.webappConnection.label') }}
      </label>
      <div class="col-span-7 w-full">
        <ComboboxSelect
          value-key="id"
          label-key="text"
          clearable
          :placeholder="t('pages.audience.audienceCreatePage.block.profil.presence.placeholder')"
          :model-value="rules.webappLogIn.value[0] ?? null"
          trigger-class="w-full"
          :disabled="disabled"
          :search-threshold="Infinity"
          :options="[
            { id: 1, text: t('pages.audience.audienceCreatePage.block.engagement.webappConnection.yes') },
            { id: 0, text: t('pages.audience.audienceCreatePage.block.engagement.webappConnection.no') }
          ]"
          @update:model-value="webappLogInDropdownUpdate"
        />
      </div>
    </div>
    <div
      :class="[
        classes.rowGrid,
        {
          'bg-blue-50/30 rounded-lg p-3 ring-1 ring-blue-600': walletInstalledHighlight.hasDefined
        }
      ]"
    >
      <label class="col-span-5 flex flex-wrap break-words text-sm font-medium leading-normal text-foreground">
        {{ t('pages.audience.audienceCreatePage.block.engagement.walletInstalled.label') }}
      </label>
      <div class="col-span-7 w-full">
        <ComboboxSelect
          value-key="id"
          label-key="text"
          clearable
          :placeholder="t('pages.audience.audienceCreatePage.block.profil.presence.placeholder')"
          :model-value="walletInstalledValue"
          trigger-class="w-full"
          :disabled="disabled"
          :search-threshold="Infinity"
          :options="walletPresenceOptions"
          @update:model-value="walletInstalledDropdownUpdate"
        />
      </div>
    </div>
  </AudienceDisclosure>
</template>
